/**
 * Injection routes, fetched once and grouped two ways.
 *
 * Same shape as `useCounterfactualExposure` and `useEntitlements`: a
 * module-level cache keyed by tenant, an inflight map so simultaneous mounts
 * share one promise, and a short TTL. A page of thirty agent cards must make
 * one request.
 *
 * Grouped by agent AND by origin because a route has two ends and two readers.
 * An agent card asks "what reaches me"; a component row asks "who reads what I
 * hold". Same rows, two indexes, one fetch.
 *
 * An absent key is NOT "no routes"
 * --------------------------------
 * `byAgent === null` means the route refused — show nothing, or a teaser where
 * there is room for one. A hint absent from a non-null index means no assessed
 * route ended at that agent, which for an agent the estate genuinely does not
 * route to is correct, and for an agent whose `agent_id_hint` predates the
 * current scan is not. The hint embeds the definition's line number, so it is a
 * within-scan key; `AgentRoutes` renders nothing rather than asserting an
 * absence it cannot stand behind.
 */
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../store'
import { attackPathsApi, AttackPath, AttackPathsResponse } from '../services/api'

interface Indexed {
  byAgent: Record<string, AttackPath[]>
  byOrigin: Record<string, AttackPath[]>
  counts: AttackPathsResponse['counts']
  scope: string | null
}

interface Result {
  byAgent: Record<string, AttackPath[]> | null
  byOrigin: Record<string, AttackPath[]> | null
  counts: AttackPathsResponse['counts'] | null
  scope: string | null
  loading: boolean
  notEntitled: boolean
}

const cache = new Map<string, { at: number; value: Indexed | null }>()
const inflight = new Map<string, Promise<Indexed | null>>()

const TTL_MS = 60_000

export const clearAttackPathsCache = () => {
  cache.clear()
  inflight.clear()
}

const index = (data: AttackPathsResponse): Indexed => {
  const byAgent: Record<string, AttackPath[]> = {}
  const byOrigin: Record<string, AttackPath[]> = {}
  ;(data.paths || []).forEach((p) => {
    if (p.agent_id_hint) (byAgent[p.agent_id_hint] ||= []).push(p)
    if (p.origin) (byOrigin[p.origin] ||= []).push(p)
  })
  return { byAgent, byOrigin, counts: data.counts, scope: data.scope }
}

const fetchPaths = (tenantId: string): Promise<Indexed | null> => {
  const hit = cache.get(tenantId)
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.value)

  const existing = inflight.get(tenantId)
  if (existing) return existing

  const p = attackPathsApi
    .paths()
    .then((resp) => {
      const value = index(resp.data)
      cache.set(tenantId, { at: Date.now(), value })
      return value as Indexed | null
    })
    .catch(() => {
      // A 404 is the ordinary case for an unentitled tenant. Cached so a page
      // of cards does not retry once per card.
      cache.set(tenantId, { at: Date.now(), value: null })
      return null
    })
    .finally(() => {
      inflight.delete(tenantId)
    })

  inflight.set(tenantId, p)
  return p
}

export const useAttackPaths = (): Result => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id
  const [value, setValue] = useState<Indexed | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!tenantId) {
      setLoading(false)
      return
    }
    let cancelled = false
    fetchPaths(tenantId).then((v) => {
      if (cancelled) return
      setValue(v)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [tenantId])

  return {
    byAgent: value?.byAgent ?? null,
    byOrigin: value?.byOrigin ?? null,
    counts: value?.counts ?? null,
    scope: value?.scope ?? null,
    loading,
    notEntitled: !loading && value === null,
  }
}
